import { useEffect } from 'react';

/**
 * Hook to check URL hash parameters and trigger actions based on them
 *
 * @param {Object} modalActions - Object mapping hash parameters to action functions
 * @param {Object} options - Additional options
 * @param {boolean} options.clearHashAfterAction - Whether to clear the hash parameter after triggering the action (default: true)
 * @returns {void}
 *
 * @example
 * useHashCheck({
 *   'add_contact': () => setShowContactModal(true),
 *   'manage_tags': () => setShowManageTagsModal(true)
 * });
 */
export const useHashCheck = (
	modalActions,
	options = { clearHashAfterAction: true },
) => {
	useEffect(() => {
		const checkHashAndTriggerActions = () => {
			const hashParams = new URLSearchParams(window.location.hash.substring(1));
			let hashChanged = false;

			// Check each modal action against hash parameters
			Object.entries(modalActions).forEach(([paramName, actionFn]) => {
				const paramValue = hashParams.get(paramName);

				if (paramValue === 'true') {
					// Execute the action function
					actionFn();

					// Remove the parameter from hash if clearHashAfterAction is true
					if (options.clearHashAfterAction) {
						hashParams.delete(paramName);
						hashChanged = true;
					}
				}
			});

			// Update the URL hash if any parameters were removed
			if (hashChanged) {
				if (hashParams.toString()) {
					window.location.hash = `#${hashParams.toString()}`;
				} else {
					// If no hash parameters remain, remove the hash from the URL
					window.history.replaceState(
						null,
						'',
						window.location.pathname + window.location.search,
					);
				}
			}
		};

		// Check hash parameters on mount
		checkHashAndTriggerActions();

		// Add event listener for hash changes
		window.addEventListener('hashchange', checkHashAndTriggerActions);

		// Clean up event listener on unmount
		return () => {
			window.removeEventListener('hashchange', checkHashAndTriggerActions);
		};
	}, [modalActions, options.clearHashAfterAction]);
};
